import { ApiResult, apiUrl } from '../../../api';
import {
  EIncomingInvoiceState,
  FinancialDocumentAllocation,
  IncomingInvoiceDto,
  UpdateIncomingInvoiceDto,
} from '../../../models/IncomingInvoices';
import { ProjectId } from '../../../models/Types';
import { ReceiptDto, VisaCardDto } from '../../../models/VisaCard';
import { getAccessToken } from '../../../store/authEffect';
import fetchWithRetry from '../../../util/fetchWithRetry';

export const sendChatMessage: (
  userMessage: string,
  type: string,
  messageId: string,
  includeDraft?: boolean,
  projectId?: ProjectId
) => Promise<ApiResult<string>> = async (
  userMessage,
  type,
  messageId,
  includeDraft,
  projectId
) => {
  const result = await fetchWithRetry(
    `${apiUrl}/cognitive/${type}/${messageId}?${
      projectId === 'me' ? '' : `projectId=${projectId}&`
    }userMessage=${encodeURIComponent(userMessage)}&outputHtmlFormat=true${
      type === 'NewEmailAi' ? `&getDraft=${includeDraft}` : ''
    }`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiGetLastAiEmailResponse: (
  messageId: string
) => Promise<ApiResult<string>> = async (messageId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/cognitive/EmailAiCache/${messageId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 && result.status !== 204
      ? await result.json()
      : null;
  return {
    result,
    data,
  };
};

export const apiResetConversation: (
  messageId: string
) => Promise<ApiResult<void>> = async (messageId) => {
  const result = await fetchWithRetry(
    `${apiUrl}/cognitive/EmailAiCache/${messageId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    }
  );
  return { result };
};

/**
 * Upload and analyze a file for invoice information
 * The analysis will happen asynchronously and return 200 with UpdateIncomingInvoiceDto
 */
export const apiUploadAndAnalyzeInvoice = async (
  file: File,
  fileLanguage: string = 'german'
): Promise<ApiResult<UpdateIncomingInvoiceDto>> => {
  const formData = new FormData();
  formData.append('invoice', file);

  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/upload?fileLanguage=${fileLanguage}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'POST',
      body: formData,
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

export const apiTriggerOCRAnalysis = async (
  incomingInvoiceId: string,
  fileLanguage: string = 'german'
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/triggerOcr?fileLanguage=${fileLanguage}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  const text =
    result.status >= 200 && result.status < 300 ? await result.text() : null;
  const data = text ? JSON.parse(text) : null;
  return {
    result,
    data,
  };
};

/**
 * Reset the analyzation object result as the creator
 */
export const apiResetAnalyzationResultAsCreator = async (
  incomingInvoiceId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/AnalyzationReset`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    }
  );
  return { result };
};

/**
 * Reset the analyzation object result as an office
 */
export const apiResetAnalyzationResultAsAnOffice = async (
  incomingInvoiceId: string,
  officeId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/office/${officeId}/AnalyzationReset`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    }
  );
  return { result };
};

/**
 * Reset the analyzation object result as a project
 */
export const apiResetAnalyzationResultAsAProject = async (
  incomingInvoiceId: string,
  projectId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/project/${projectId}/AnalyzationReset`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
    }
  );
  return { result };
};

/**
 * Create a new incoming invoice without uploading a file
 */
export const apiCreateIncomingInvoiceWithoutFile = async (
  officeId: string
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const requestBody: Partial<IncomingInvoiceDto> = {};

  if (officeId) {
    requestBody.toOfficeId = officeId;
    requestBody.sharedWithOfficeIds = [officeId];
  }

  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(requestBody),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Get incoming invoices as the creator (user's own invoices)
 */
export const apiGetIncomingInvoicesAsCreator = async (
  from?: Date,
  to?: Date
): Promise<ApiResult<IncomingInvoiceDto[]>> => {
  const params = new URLSearchParams();
  if (from) params.append('from', from.toISOString());
  if (to) params.append('to', to.toISOString());

  const queryString = params.toString();
  const url = `${apiUrl}/accounting/v2/IncomingInvoice${queryString ? `?${queryString}` : ''}`;

  const result = await fetchWithRetry(url, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * 
 Get incoming invoices for a office
 */
export const apiGetIncomingInvoicesAsOffice = async (
  officeId: string,
  from?: Date,
  to?: Date
): Promise<ApiResult<IncomingInvoiceDto[]>> => {
  const params = new URLSearchParams();
  if (from) params.append('from', from.toISOString());
  if (to) params.append('to', to.toISOString());

  const queryString = params.toString();
  const url = `${apiUrl}/accounting/v2/IncomingInvoice/office/${officeId}${queryString ? `?${queryString}` : ''}`;

  const result = await fetchWithRetry(url, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Get incoming invoices for a specific project
 */
export const apiGetIncomingInvoicesAsProject = async (
  projectId: ProjectId,
  from?: Date,
  to?: Date
): Promise<ApiResult<IncomingInvoiceDto[]>> => {
  const params = new URLSearchParams();
  if (from) params.append('from', from.toISOString());
  if (to) params.append('to', to.toISOString());

  const queryString = params.toString();
  const url = `${apiUrl}/accounting/v2/IncomingInvoice/project/${projectId}${queryString ? `?${queryString}` : ''}`;

  const result = await fetchWithRetry(url, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Get a specific incoming invoice as the creator
 */
export const apiGetIncomingInvoiceAsCreator = async (
  incomingInvoiceId: string
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Get a specific incoming invoice as a project
 */
export const apiGetIncomingInvoiceAsProject = async (
  incomingInvoiceId: string,
  projectId: ProjectId
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/project/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Update an existing incoming invoice as the creator
 */
export const apiUpdateIncomingInvoiceAsCreator = async (
  incomingInvoiceId: string,
  invoiceData: UpdateIncomingInvoiceDto
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(invoiceData),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Update an existing incoming invoice as office
 */
export const apiUpdateIncomingInvoiceAsOffice = async (
  incomingInvoiceId: string,
  officeId: string,
  invoiceData: UpdateIncomingInvoiceDto
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/office/${officeId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(invoiceData),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Update an existing incoming invoice as a project
 */
export const apiUpdateIncomingInvoiceAsProject = async (
  incomingInvoiceId: string,
  projectId: ProjectId,
  invoiceData: UpdateIncomingInvoiceDto
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/project/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(invoiceData),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Request approval for an incoming invoice
 */
export const apiRequestApprovalIncomingInvoice = async (
  incomingInvoiceId: string
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/request-approval`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Approve an incoming invoice
 */
export const apiApproveIncomingInvoice = async (
  incomingInvoiceId: string,
  officeId: string
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/office/${officeId}/approve`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Reject an incoming invoice
 */
export const apiRejectIncomingInvoice = async (
  incomingInvoiceId: string,
  officeId: string
): Promise<ApiResult<IncomingInvoiceDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/office/${officeId}/reject`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Delete an incoming invoice as the creator
 */
export const apiDeleteIncomingInvoiceAsCreator = async (
  incomingInvoiceId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    }
  );

  return { result };
};

export const apiDeleteIncomingInvoiceAsOffice = async (
  incomingInvoiceId: string,
  officeId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/office/${officeId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    }
  );

  return { result };
};

/**
 * Delete an incoming invoice as a project
 */
export const apiDeleteIncomingInvoiceAsProject = async (
  incomingInvoiceId: string,
  projectId: ProjectId
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/project/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    }
  );

  return { result };
};

/**
 * Download the invoice file as the creator
 */
export const apiDownloadInvoiceFileAsCreator = async (
  incomingInvoiceId: string
): Promise<ApiResult<Blob>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/file`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.blob() : null;

  return {
    result,
    data,
  };
};

/**
 * Download the invoice file as a project
 */
export const apiDownloadInvoiceFileAsProject = async (
  incomingInvoiceId: string,
  projectId: ProjectId
): Promise<ApiResult<Blob>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/project/${projectId}/file`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.blob() : null;
  return {
    result,
    data,
  };
};

/**
 * Download the invoice file as an office
 */
export const apiDownloadInvoiceFileAsOffice = async (
  incomingInvoiceId: string,
  officeId: string
): Promise<ApiResult<Blob>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/office/${officeId}/file`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'GET',
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.blob() : null;
  return {
    result,
    data,
  };
};

/**
 * Override/replace the file for an existing incoming invoice
 */
export const apiOverrideInvoiceFile = async (
  incomingInvoiceId: string,
  file: File
): Promise<ApiResult<UpdateIncomingInvoiceDto>> => {
  const formData = new FormData();
  formData.append('invoice', file);

  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/upload`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'POST',
      body: formData,
    }
  );

  const text =
    result.status >= 200 && result.status < 300 ? await result.text() : null;
  const data = text ? JSON.parse(text) : null;

  return {
    result,
    data,
  };
};

/**
 * Helper function to check if an invoice is still being processed (background task)
 * This can be used to poll for updates when upload returns 200 but analysis is ongoing
 */
export const apiCheckInvoiceProcessingStatus = async (
  incomingInvoiceId: string,
  projectId?: ProjectId
): Promise<ApiResult<IncomingInvoiceDto>> => {
  // Use the appropriate get method based on whether we have a projectId
  if (projectId && projectId !== 'me') {
    return apiGetIncomingInvoiceAsProject(incomingInvoiceId, projectId);
  } else {
    return apiGetIncomingInvoiceAsCreator(incomingInvoiceId);
  }
};

/**
 * Helper function to poll for invoice analysis completion
 * Returns a promise that resolves when the invoice analysis is complete
 */
export const apiWaitForInvoiceAnalysis = async (
  incomingInvoiceId: string,
  projectId?: ProjectId,
  maxAttempts: number = 30,
  intervalMs: number = 2000
): Promise<ApiResult<IncomingInvoiceDto>> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await apiCheckInvoiceProcessingStatus(
      incomingInvoiceId,
      projectId
    );

    if (!result.result.ok) {
      return result;
    }

    // Check if analysis is complete (you may need to adjust this condition based on your API)
    // Assuming the invoice state changes from 'processing' when analysis is complete
    if (
      result.data &&
      result.data.incomingInvoiceState !== EIncomingInvoiceState.Processing
    ) {
      return result;
    }

    // Wait before next attempt
    if (attempt < maxAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  // If we've exceeded max attempts, return the last result
  return apiCheckInvoiceProcessingStatus(incomingInvoiceId, projectId);
};

/**
 * Get all financial document allocations for a specific project and financial document
 */
export const apiGetAllocationsForFinancialDocument = async (
  projectId: string,
  financialDocumentId: string
): Promise<ApiResult<FinancialDocumentAllocation[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/project/${projectId}/FinancialDocumentAllocation/financial-document/${financialDocumentId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

/**
 * Create new financial document allocations for a specific project and financial document
 */
export const apiCreateAllocations = async (
  projectId: string,
  financialDocumentId: string,
  allocations: FinancialDocumentAllocation[]
): Promise<ApiResult<FinancialDocumentAllocation[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/project/${projectId}/FinancialDocumentAllocation/financial-document/${financialDocumentId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(allocations),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

/**
 * Update existing financial document allocations for a specific project and financial document
 */
export const apiUpdateAllocations = async (
  projectId: string,
  financialDocumentId: string,
  allocations: FinancialDocumentAllocation[]
): Promise<ApiResult<FinancialDocumentAllocation[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/project/${projectId}/FinancialDocumentAllocation/financial-document/${financialDocumentId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(allocations),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;

  return {
    result,
    data,
  };
};

/**
 * Delete specific financial document allocations for a project and financial document
 */
export const apiDeleteAllocations = async (
  projectId: string,
  financialDocumentId: string,
  allocationIdsToDelete: string[]
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/project/${projectId}/FinancialDocumentAllocation/financial-document/${financialDocumentId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
      body: JSON.stringify(allocationIdsToDelete),
    }
  );

  return {
    result,
    data: null,
  };
};

//#region ------------------------------ VisaCard API

/**
 * Get all visa cards owned by the authenticated user
 */
export const apiGetMyVisaCards = async (): Promise<
  ApiResult<VisaCardDto[]>
> => {
  const result = await fetchWithRetry(`${apiUrl}/accounting/VisaCard/my`, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return { result, data };
};

/**
 * Get all visa cards for a specific office
 */
export const apiGetVisaCardsByOffice = async (
  officeId: string
): Promise<ApiResult<VisaCardDto[]>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/office/${officeId}/VisaCard`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return { result, data };
};

/**
 * Upload a receipt file for the current user with visa card association
 */
export const apiUploadReceipt = async (
  file: File,
  visaCardId: string,
  ocrLanguage: string = 'german'
): Promise<ApiResult<ReceiptDto>> => {
  const formData = new FormData();
  formData.append('File', file, file.name);

  const params = new URLSearchParams();
  params.append('ReceiptType', 'visa');
  params.append('VisaCardId', visaCardId);
  if (ocrLanguage) params.append('ocrLanguage', ocrLanguage);

  const result = await fetchWithRetry(
    `${apiUrl}/accounting/Receipt?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
      },
      method: 'POST',
      body: formData,
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return { result, data };
};

//#endregion

//#region ------------------------------ Duplicate Check API

export interface DuplicateInvoiceSuggestion {
  createdAt: string;
  approvedAt: string;
  totalIncludingVat: number;
  incomingInvoiceState: EIncomingInvoiceState;
  invoiceNumber: string;
}

export interface DuplicateCheckResult {
  duplicateInvoicesSuggestion: DuplicateInvoiceSuggestion[];
}

export const apiDuplicateCheckIncomingInvoice = async (
  incomingInvoiceId: string
): Promise<ApiResult<DuplicateCheckResult>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/accounting/v2/IncomingInvoice/${incomingInvoiceId}/duplicateCheck`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'GET',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

//#endregion

//#region ------------------------------ PrioShare API (re-exported from prioShare.ts)
export {
  apiGetPrioShares,
  apiDeactivatePrioShare,
  apiCreatePrioShareDraft,
  apiPublishPrioShare,
  apiUploadPrioShareFile,
  apiDeletePrioShareFile,
  apiDeletePrioShareDraft,
  apiSavePrioShareDraft,
  apiRecreatePrioShare,
} from './prioShare';
//#endregion
